<template>
  <v-carousel
    :continuous="true"
    :cycle="cycle"
    :show-arrows="slides.length > 1"
    hide-delimiters
    height="auto"
  >
    <v-carousel-item v-for="(slide, i) in slides" :key="i">
      <v-card
        class="message-card mx-auto d-flex flex-column"
        :width="cardWidth(slide)"
        rounded="lg"
        elevation="8"
      >
        <!-- Image message (type 3) -->
        <template
          v-if="slide.messageCenterMessageTypeId === MESSAGE_TYPE_ID_IMAGE"
        >
          <div class="message-card__header d-flex align-center px-6 pt-6 pb-4">
            <v-img
              src="/images/e360_Logo.svg"
              alt="Energy 360"
              width="104"
              :aspect-ratio="258 / 80.5"
              class="mr-4 flex-shrink-0 flex-grow-0"
            />
            <h2
              class="message-card__title text-primary font-weight-bold flex-grow-1 text-center"
              :class="smallBreakpoint ? 'text-h6' : 'text-h5'"
              v-html="slide.messageCenterMessageLabel"
            />
            <!-- Mirrors the logo so the title centers on the card, not on the
                 leftover space. Dropped on phones to give the title room. -->
            <div
              class="message-card__logo-spacer flex-shrink-0 d-none d-sm-block"
            />
          </div>

          <v-divider />

          <div
            class="px-6 pt-6"
            :class="{ 'pb-6': !imageViews[i].captionHtml }"
          >
            <v-img
              class="message-card__image"
              :src="imageViews[i].imageSrc"
              :aspect-ratio="16 / 9"
              cover
              rounded="lg"
            >
              <template v-slot:placeholder>
                <v-row class="fill-height ma-0" align="center" justify="center">
                  <v-progress-circular indeterminate color="grey-lighten-1" />
                </v-row>
              </template>
              <template v-slot:error>
                <v-img
                  :src="DEFAULT_MESSAGE_IMAGE_SRC"
                  :aspect-ratio="16 / 9"
                  cover
                />
              </template>
            </v-img>
          </div>

          <!-- mt-6 matches the pt-6 above the image, and is a MARGIN on purpose:
               padding on a scroll container scrolls away with the content, so
               the caption would slide up under the image. The margin sits
               outside the scroll area, so the text always stops at that gap. -->
          <div
            v-if="imageViews[i].captionHtml"
            class="message-card__body px-6 mt-6 pb-5"
          >
            <div
              class="message-card__text text-body-1"
              v-html="imageViews[i].captionHtml"
            />
          </div>
        </template>

        <!-- Text message (type 2; also the fallback so previews of any
             non-image type render) -->
        <template v-else>
          <div class="message-card__header d-flex align-center px-6 pt-6 pb-4">
            <v-avatar
              color="primary"
              variant="tonal"
              rounded="lg"
              size="44"
              class="mr-3 flex-shrink-0"
            >
              <v-icon size="22">mdi-email-outline</v-icon>
            </v-avatar>
            <h2
              class="message-card__title text-primary font-weight-bold flex-grow-1"
              :class="smallBreakpoint ? 'text-h6' : 'text-h5'"
              v-html="slide.messageCenterMessageLabel"
            />
          </div>

          <v-divider />

          <div class="message-card__body px-6 py-6">
            <div
              class="message-card__text text-body-1"
              v-html="slide.messagingCenterMessageValue"
            />
          </div>
        </template>

        <!-- Footer -->
        <v-divider />
        <v-card-actions class="px-6 py-3">
          <v-spacer />
          <v-btn
            color="primary"
            variant="flat"
            rounded="lg"
            class="px-5"
            @click="preview ? emits('close:dialog') : markMessageAsRead(slide)"
            :loading="isLoading"
          >
            {{ preview ? 'Close Preview' : 'Mark as Read' }}
            <v-icon end>
              {{ preview ? 'mdi-close' : 'mdi-email-open-outline' }}
            </v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-carousel-item>
  </v-carousel>
</template>
<script setup lang="ts">
import API from '@/Core.Service.Domain/MessageCenter/Users/API';

import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useDisplay } from 'vuetify';

import {
  DEFAULT_MESSAGE_IMAGE_SRC,
  MESSAGE_TYPE_ID_IMAGE,
  parseMessageImage,
  resolveMessageImageSrc,
} from '@/Core.Service.Domain/MessageCenter/MessageCenter.Common/utils/messageImage';

import { ISlide } from '@/Core.Service.Domain/MessageCenter/Users/Components/types/Slide.ts';

// preview: renders the same card from unsaved composer state — the footer
// closes the dialog instead of marking anything as read.
const props = defineProps<{
  slides: ISlide[];
  preview?: boolean;
}>();

const emits = defineEmits(['close:dialog']);

const { smAndDown: smallBreakpoint } = useDisplay();
const cycle = ref(false);
const isLoading = ref(false);

// Size the card to the message kind: images need more room than text,
// and both go near full-width on small screens.
const cardWidth = (slide: ISlide) =>
  smallBreakpoint.value
    ? '92vw'
    : slide.messageCenterMessageTypeId === MESSAGE_TYPE_ID_IMAGE
      ? 820
      : 600;

// The image key travels as a token inside the message body (no backend
// image field yet) — split it into the image source and the clean caption
// once per slide.
const imageViews = computed(() =>
  props.slides.map((slide) => {
    const { imageKey, cleanValue } = parseMessageImage(
      slide.messagingCenterMessageValue
    );
    return {
      imageSrc: resolveMessageImageSrc(imageKey),
      captionHtml: cleanValue,
    };
  })
);

const store = useStore();
// Refresh the badge count AND the message list. The list feeds both the
// My Messages rail and the pop-up filter in MessageCenterEntryPoint, so a
// stale list kept this overlay mounted and popped the message up again on
// the next route change (sc-22735 follow-up).
const refreshMessages = async () => {
  try {
    isLoading.value = true;
    await Promise.all([
      store.dispatch('MessageCenterUsersStore/setUnreadMessagesCount'),
      store.dispatch('MessageCenterUsersStore/setMessagesByUser'),
    ]);
  } catch (error) {
    return Promise.reject(error);
  } finally {
    isLoading.value = false;
  }
};

const markMessageAsRead = async (message: ISlide) => {
  try {
    isLoading.value = true;
    const messageId = message.userMessageCenterMessageId;
    await API.markMessageAsRead(messageId);
    await refreshMessages();
    emits('close:dialog');
  } catch (error) {
    return Promise.reject(error);
  } finally {
    isLoading.value = false;
  }
};
</script>
<style lang="scss" scoped>
// Card sizes to its content (no forced empty space) and caps at the viewport,
// letting only the message body scroll when a message is long.
.message-card {
  max-height: 85vh;
  overflow: hidden;
}

.message-card__title {
  line-height: 1.4;
  word-break: break-word;
}

// Scrollbar stays hidden; the shadow below is the scroll affordance instead.
//
// Pure CSS, no scroll listener. The two `local` layers are painted in the
// card's own surface colour and scroll WITH the text; the two `scroll` layers
// are soft shadows pinned to the container. At the top of the caption the
// first cover sits over the top shadow and hides it, and at the very bottom
// the second hides the bottom one — so a shadow shows exactly when there is
// more text in that direction, and a caption that already fits shows neither.
.message-card__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;

  background:
    linear-gradient(
        rgb(var(--v-theme-surface)) 30%,
        rgba(var(--v-theme-surface), 0)
      )
      center top,
    linear-gradient(
        rgba(var(--v-theme-surface), 0),
        rgb(var(--v-theme-surface)) 70%
      )
      center bottom,
    radial-gradient(
        farthest-side at 50% 0,
        rgba(0, 0, 0, 0.18),
        rgba(0, 0, 0, 0)
      )
      center top,
    radial-gradient(
        farthest-side at 50% 100%,
        rgba(0, 0, 0, 0.18),
        rgba(0, 0, 0, 0)
      )
      center bottom;
  background-repeat: no-repeat;
  background-size:
    100% 36px,
    100% 36px,
    100% 14px,
    100% 14px;
  background-attachment: local, local, scroll, scroll;
}

.message-card__body::-webkit-scrollbar {
  display: none;
}

// Readable measure, centered as a column, with the composer's line breaks kept.
.message-card__text {
  max-width: 62ch;
  margin: 0 auto;
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-word;
  color: rgba(0, 0, 0, 0.82);

  :deep(p) {
    margin-bottom: 0.75rem;
  }
}

// Image message: the picture sits in a framed inset instead of full bleed.
.message-card__image {
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: rgba(0, 0, 0, 0.03);
}

// Same footprint as the logo + its margin, on the opposite side.
.message-card__logo-spacer {
  width: 104px;
  margin-left: 16px;
}
</style>
